import React, { useEffect, useState } from "react"
import {
  Col,
  Row,
  Button,
  TabPane,
  Form,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
} from "reactstrap"

const CPSSampleTab = ({ nextTab, previousTab, request, setRequest }) => {
  const [sampleQuantityError, setSampleQuantityError] = useState("")

  const handleInputChange = (field, value) => {
    setRequest({ ...request, [field]: value })
  }

  const handleSampleQuantityChange = value => {
    if (value === "") {
      setSampleQuantityError("")
      setRequest({ ...request, sampleQuantity: "" })
      return
    }

    const numericValue = parseInt(value, 10)

    if (Number.isNaN(numericValue) || numericValue <= 0) {
      setSampleQuantityError("Please enter a valid quantity (1-10 pcs).")
      setRequest({ ...request, sampleQuantity: "" })
      return
    }

    if (numericValue > 10) {
      setSampleQuantityError("Maximum 10 pieces allowed for samples.")
      setRequest({ ...request, sampleQuantity: 10 })
      return
    }

    setSampleQuantityError("")
    setRequest({ ...request, sampleQuantity: numericValue })
  }

  const handleSampleRequiredChange = value => {
    setRequest({
      ...request,
      sampleRequired: value,
      // Reset sample fields if not required
      ...(value
        ? {}
        : {
            sampleQuantity: "",
            samplePrice: "",
            sampleDeliveryDate: "",
          }),
    })
  }

  // Handle default sample requirements based on CPS type
  useEffect(() => {
    if (request.type === "Programming") {
      // For Programming, always require samples
      if (request.sampleRequired === false) {
        setRequest({ ...request, sampleRequired: true })
      }
    } else if (request.type === "Sleeving") {
      // For Sleeving, default to no sample required (proceed directly)
      if (request.sampleRequired === true) {
        setRequest({ ...request, sampleRequired: false })
      }
    }
  }, [request.type])

  return (
    <TabPane tabId={3}>
      <div>
        <div className="text-center mb-4">
          <h5 className="card-title">Sample Requirements</h5>
          <p className="card-title-desc">
            Do you need a sample batch before full production?
          </p>
        </div>

        <Row className="justify-content-center">
          <Col lg="8">
            <Card>
              <CardBody>
                <Form>
                  <FormGroup className="mb-4">
                    <Label>Sample Required *</Label>
                    <div className="mt-2">
                      <div className="form-check form-check-inline">
                        <Input
                          className="form-check-input"
                          type="radio"
                          name="sampleRequired"
                          id="sampleRequiredYes"
                          checked={request.sampleRequired === true}
                          onChange={() => handleSampleRequiredChange(true)}
                        />
                        <Label
                          className="form-check-label"
                          htmlFor="sampleRequiredYes"
                        >
                          Yes - I need a sample batch first
                        </Label>
                      </div>
                      {request.type !== "Programming" && (
                        <div className="form-check form-check-inline">
                          <Input
                            className="form-check-input"
                            type="radio"
                            name="sampleRequired"
                            id="sampleRequiredNo"
                            checked={request.sampleRequired === false}
                            onChange={() => handleSampleRequiredChange(false)}
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="sampleRequiredNo"
                          >
                            No - Proceed directly to production
                          </Label>
                        </div>
                      )}
                    </div>
                  </FormGroup>

                  {request.sampleRequired === true && (
                    <div className="bg-light p-3 rounded">
                      <h6 className="mb-3">Sample Configuration</h6>

                      <Row>
                        <Col md="6">
                          <FormGroup className="mb-3">
                            <Label htmlFor="sampleQuantity">
                              Sample Quantity
                            </Label>
                            <Input
                              type="number"
                              id="sampleQuantity"
                              placeholder="Number of sample units"
                              value={request.sampleQuantity}
                              onChange={e =>
                                handleSampleQuantityChange(e.target.value)
                              }
                              min="1"
                            />
                            <div className="form-text text-muted">
                              Maximum 10 pieces allowed.
                            </div>
                            {sampleQuantityError && (
                              <div className="text-danger small mt-1">
                                {sampleQuantityError}
                              </div>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup className="mb-3">
                            <Label htmlFor="sampleDeliveryDate">
                              Desired Sample Delivery Date
                            </Label>
                            <Input
                              type="date"
                              id="sampleDeliveryDate"
                              value={request.sampleDeliveryDate}
                              onChange={e =>
                                handleInputChange(
                                  "sampleDeliveryDate",
                                  e.target.value
                                )
                              }
                            />
                        </FormGroup>
                        </Col>
                      </Row>

                      
                    </div>
                  )}

                  <FormGroup className="mb-3">
                    <Label htmlFor="desiredDeliveryDate">
                      Desired Production Delivery Date
                    </Label>
                    <Input
                      type="date"
                      id="desiredDeliveryDate"
                      value={request.desiredDeliveryDate}
                      onChange={e =>
                        handleInputChange("desiredDeliveryDate", e.target.value)
                      }
                    />
                    <div className="form-text text-muted">
                      {request.sampleRequired
                        ? "Delivery date for main production batch (after sample approval)"
                        : "Delivery date for the entire order"}
                    </div>
                  </FormGroup>

                  {request.sampleRequired === false &&
                    request.type !== "Programming" && (
                      <div className="alert alert-info">
                        <i className="bx bx-info-circle me-2"></i>
                        You have chosen to proceed directly to production
                        without samples. The full quantity will be processed
                        according to your specifications.
                      </div>
                    )}

                  {request.sampleRequired === true && (
                    <div className="alert alert-warning">
                      <i className="bx bx-error-circle me-2"></i>
                      Sample approval is required before proceeding to full
                      production. This may extend the overall delivery timeline.
                    </div>
                  )}
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <div className="d-flex justify-content-between mt-4">
        <Button color="secondary" onClick={previousTab}>
          Previous
        </Button>
        <Button
          color="primary"
          onClick={() => {
            // Block navigation if sampleRequired and quantity is invalid
            if (
              request.sampleRequired === true &&
              (!request.sampleQuantity ||
                parseInt(request.sampleQuantity, 10) > 10)
            ) {
              setSampleQuantityError("Maximum 10 pieces allowed for samples.")
              return
            }
            nextTab(1)
          }}
          disabled={
            request.sampleRequired === true &&
            (!request.sampleQuantity ||
              parseInt(request.sampleQuantity, 10) > 10)
          }
        >
          Next - Confirmation
        </Button>
      </div>
    </TabPane>
  )
}

export default CPSSampleTab
